/**
 * TCB R37 Tax / Resale / Commercial Document runtime adapter.
 * Extends R36 company-location identities — not a parallel B2B/quote/tax engine.
 * Uploads never auto-grant exemption. Unknown remains unknown.
 */
(function (global) {
  'use strict';

  var CFG = global.TCB_TAX_DOCUMENT_INTELLIGENCE || null;
  var EXEMPT_OK = 'EXEMPT_VERIFIED';

  function loadConfig(cb) {
    if (!CFG && global.TCB_TAX_DOCUMENT_INTELLIGENCE) CFG = global.TCB_TAX_DOCUMENT_INTELLIGENCE;
    if (CFG) {
      cb(null, CFG);
      return;
    }
    var node = document.querySelector('[data-tcb-tax-runtime-url]');
    var url = node && node.getAttribute('data-tcb-tax-runtime-url');
    if (!url || !global.fetch) {
      cb(new Error('tax_runtime_config_missing'));
      return;
    }
    global
      .fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        CFG = json;
        global.TCB_TAX_DOCUMENT_INTELLIGENCE = json;
        cb(null, json);
      })
      .catch(function (err) {
        cb(err || new Error('tax_runtime_fetch_failed'));
      });
  }

  function getProfile(companyId, locationId) {
    return ((CFG && CFG.tax_profiles) || []).find(function (p) {
      return p.company_id === companyId && p.company_location_id === locationId;
    }) || null;
  }

  function getCertificates(companyId, locationId) {
    return ((CFG && CFG.certificates) || []).filter(function (c) {
      return c.company_id === companyId && (!locationId || c.company_location_id === locationId);
    });
  }

  function assertLocationScope(certificate, locationId) {
    if (!certificate) return { ok: false, reason: 'missing_certificate' };
    if (certificate.company_location_id !== locationId) {
      return { ok: false, reason: 'location_scope_mismatch' };
    }
    var scope = certificate.scope || {};
    if (scope.company_wide === true) {
      return { ok: true, company_wide: true };
    }
    if ((scope.location_ids || []).indexOf(locationId) === -1 && certificate.company_location_id !== locationId) {
      return { ok: false, reason: 'certificate_not_in_scope' };
    }
    return { ok: true };
  }

  /** Intake always PENDING_REVIEW — never auto-verify. */
  function intakeCertificate(payload) {
    payload = payload || {};
    if (!payload.company_id || !payload.company_location_id || !payload.type) {
      return { ok: false, status: 'invalid', reasons: ['missing_required_fields'] };
    }
    return {
      ok: true,
      status: 'PENDING_REVIEW',
      customer_visible_status: 'Review Pending',
      auto_granted: false,
      tax_state: 'EXEMPT_PENDING_REVIEW',
      write_allowed: false,
      message: 'Upload received for review. Exemption is not granted until staff verification.'
    };
  }

  function isExpired(cert, asOf) {
    if (!cert || !cert.expires_at) return false;
    var when = asOf ? Date.parse(asOf) : Date.now();
    return Date.parse(cert.expires_at) < when;
  }

  function evaluateLocationTax(companyId, locationId, asOf) {
    var profile = getProfile(companyId, locationId);
    if (!profile) {
      return {
        ok: false,
        state: 'UNKNOWN',
        customer_label: 'Not on File',
        review_required: true,
        reasons: ['no_tax_profile'],
        write_allowed: false
      };
    }

    var reasons = [];
    var state = profile.state || 'UNKNOWN';
    var certs = getCertificates(companyId, locationId);
    var verified = certs.filter(function (c) {
      return c.status === 'VERIFIED' && assertLocationScope(c, locationId).ok && !isExpired(c, asOf);
    });

    if (state === EXEMPT_OK) {
      if (!verified.length) {
        state = 'UNKNOWN';
        reasons.push('verified_state_without_valid_evidence');
      }
      verified.forEach(function (c) {
        if (isExpired(c, asOf)) {
          state = 'EXEMPT_EXPIRED';
          reasons.push('evidence_expired:' + c.certificate_id);
        }
      });
    }

    if (state === 'EXEMPT_PENDING_REVIEW') {
      reasons.push('pending_review_never_exempt');
    }
    if (state === 'UNKNOWN') {
      reasons.push('unknown_remains_unknown');
    }

    var writeAllowed =
      state === EXEMPT_OK &&
      verified.length > 0 &&
      !(profile.shopify_tax_adapter && profile.shopify_tax_adapter.write_allowed === false && CFG && CFG.shopify_tax_adapter && CFG.shopify_tax_adapter.policy && CFG.shopify_tax_adapter.policy.live_writes_enabled === false);

    // Even when evidence is verified, live Shopify writes stay gated by adapter policy.
    var liveWrites = !!(CFG && CFG.shopify_tax_adapter && CFG.shopify_tax_adapter.policy && CFG.shopify_tax_adapter.policy.live_writes_enabled);
    writeAllowed = state === EXEMPT_OK && verified.length > 0 && liveWrites;

    return {
      ok: state === EXEMPT_OK,
      state: state,
      customer_label: profile.customer_label || state,
      company_id: companyId,
      company_location_id: locationId,
      evidence_ids: (verified.length ? verified : certs).map(function (c) {
        return c.certificate_id;
      }),
      review_required: state !== EXEMPT_OK && state !== 'TAXABLE' && state !== 'NOT_APPLICABLE',
      reasons: reasons,
      write_allowed: writeAllowed,
      tax_free_pricing_allowed: state === EXEMPT_OK
    };
  }

  function quoteTaxReadiness(companyId, locationId, quoteId, asOf) {
    var evalResult = evaluateLocationTax(companyId, locationId, asOf);
    var reviewRequired = !evalResult.ok;
    var reason = null;
    if (evalResult.state === 'EXEMPT_PENDING_REVIEW') reason = 'certificate_pending_review';
    else if (evalResult.state === 'EXEMPT_EXPIRED') reason = 'certificate_expired';
    else if (evalResult.state === 'UNKNOWN') reason = 'tax_status_unknown';
    else if (evalResult.state === 'EXEMPT_REVOKED') reason = 'certificate_revoked';
    else if (reviewRequired) reason = 'tax_review_required';

    return {
      snapshot: {
        snapshot_id: 'snap_' + (quoteId || 'quote') + '_' + (locationId || 'loc'),
        quote_or_order_id: quoteId || null,
        company_id: companyId,
        company_location_id: locationId,
        tax_state: evalResult.state,
        evidence_ids: evalResult.evidence_ids || [],
        evidence_valid_at: asOf || new Date().toISOString().slice(0, 10),
        review_required: reviewRequired && evalResult.state !== 'TAXABLE',
        review_reason: reason,
        commercially_valid: true,
        tax_free_pricing_promised: false,
        created_at: new Date().toISOString()
      },
      ready_for_tax_exempt_conversion: evalResult.ok,
      tax_free_pricing_promised: false,
      evaluation: evalResult
    };
  }

  function canWriteShopifyTaxSettings(companyId, locationId, reviewer) {
    var evalResult = evaluateLocationTax(companyId, locationId);
    var guards = [];
    if (!reviewer) guards.push('missing_reviewer_identity');
    if (!evalResult.ok) guards.push('location_not_exempt_verified');
    if (!(CFG && CFG.shopify_tax_adapter && CFG.shopify_tax_adapter.policy && CFG.shopify_tax_adapter.policy.live_writes_enabled)) {
      guards.push('live_writes_disabled');
    }
    var profile = getProfile(companyId, locationId);
    if (profile && profile.shopify_tax_adapter && profile.shopify_tax_adapter.mismatch) {
      guards.push('mismatch_requires_queue_no_silent_overwrite');
    }
    return {
      ok: guards.length === 0,
      guards: guards,
      preview: {
        before: profile && profile.shopify_tax_adapter ? profile.shopify_tax_adapter.last_known_shopify_state : 'unknown',
        after: evalResult.ok ? { taxExempt: true } : { taxExempt: false }
      },
      evaluation: evalResult
    };
  }

  function detectPublicDocumentLeak(blob) {
    var text = typeof blob === 'string' ? blob : JSON.stringify(blob || CFG || {});
    var leaks = [];
    if (/https?:\/\/[^\s"]+\.(pdf|png|jpe?g|docx?)/i.test(text)) leaks.push('public_document_url');
    if (/\"storage_ref\"\s*:\s*\"https?:/i.test(text)) leaks.push('http_storage_ref');
    if (/assets\/.*cert.*\.(pdf|png)/i.test(text)) leaks.push('theme_asset_certificate');
    return { ok: leaks.length === 0, leaks: leaks };
  }

  function track(eventName, payload) {
    var safe = Object.assign({}, payload || {});
    delete safe.certificate_number;
    delete safe.tax_registration_id;
    delete safe.storage_ref;
    delete safe.document_bytes;
    delete safe.tax_id;
    var detail = Object.assign({ event: eventName, at: new Date().toISOString() }, safe);
    try {
      if (global.TCBAnalytics && typeof global.TCBAnalytics.track === 'function') {
        global.TCBAnalytics.track(eventName, detail);
      }
      global.dispatchEvent(new CustomEvent('tcb:tax', { detail: detail }));
    } catch (e) {
      /* no-op */
    }
  }

  var api = {
    version: 'r37.1.0',
    loadConfig: loadConfig,
    getProfile: getProfile,
    getCertificates: getCertificates,
    intakeCertificate: intakeCertificate,
    evaluateLocationTax: evaluateLocationTax,
    quoteTaxReadiness: quoteTaxReadiness,
    canWriteShopifyTaxSettings: canWriteShopifyTaxSettings,
    detectPublicDocumentLeak: detectPublicDocumentLeak,
    assertLocationScope: assertLocationScope,
    track: track
  };

  global.TCBTaxDocumentRuntime = api;
})(typeof window !== 'undefined' ? window : globalThis);
